import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner, Empty } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { FileText, ShieldCheck, Eye, AlertTriangle, Search } from "lucide-react";

const ESTADO = { emitido: "Pendiente de firma", firmado: "Firmado", reemplazado: "Versión anterior" };
const INTEG = {
  verificado: ["Íntegro", "border-emerald-200 bg-emerald-50 text-emerald-700"],
  alterado: ["Alterado", "border-red-200 bg-red-50 text-red-700"],
  no_disponible: ["No disponible", "border-red-200 bg-red-50 text-red-700"],
  inconsistencia_partes: ["Inconsistencia", "border-red-200 bg-red-50 text-red-700"],
  legado_firmado: ["Revisar (sistema anterior)", "border-amber-200 bg-amber-50 text-amber-700"],
  requiere_reemision: ["Requiere nueva versión", "border-amber-200 bg-amber-50 text-amber-700"],
};

function Resumen({ r }) {
  return (
    <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4 text-xs shadow-sm" data-testid="integridad-resumen">
      {Object.entries(r).map(([k, v]) => <span key={k} className={`rounded-full border px-2.5 py-1 font-semibold ${(INTEG[k] || INTEG.verificado)[1]}`}>{(INTEG[k] || [k])[0]}: {v}</span>)}
    </div>
  );
}

export default function Documentos() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [view, setView] = useState(null);
  const load = () => api.get("/documentos/registro").then((r) => setRows(r.data)).catch(() => setRows(false));
  useEffect(() => { load(); }, []);
  const verificar = async () => {
    setBusy(true);
    try { const { data } = await api.post("/documentos/integridad"); setRes(data.resumen); toast.success("Verificación completada"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (rows === false) return <Empty icon={FileText} title="Sin acceso" subtitle="Tu rol no tiene acceso al repositorio de documentos de contratación." />;
  const t = q.trim().toLowerCase();
  const lista = (rows || []).filter((r) => !t || [r.empleado, r.identificacion, r.numero_contrato, r.nombre, r.id].join(" ").toLowerCase().includes(t));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Documentos</h1>
          <p className="mt-1 text-sm text-slate-500">Repositorio central: un documento, una versión oficial y una misma huella SHA-256 para HUMAN y el empleado.</p>
        </div>
        <button onClick={verificar} disabled={busy} data-testid="btn-verificar-integridad" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{busy ? <Spinner /> : <ShieldCheck className="h-4 w-4" />} Verificar integridad</button>
      </div>
      {res && <Resumen r={res} />}
      <div className="relative max-w-sm"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empleado, cédula, contrato…" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-vino-500" data-testid="documentos-buscar" /></div>
      {!rows ? <Spinner className="mx-auto h-6 w-6 text-vino-700" /> : lista.length === 0 ? <Empty icon={FileText} title="Sin documentos" subtitle="Los documentos emitidos en Contrataciones aparecerán aquí." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="documentos-tabla">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{["Empleado", "Documento", "Versión", "Estado", "SHA-256", "Integridad", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {lista.map((r) => {
                const ig = r.integridad && INTEG[r.integridad.estado];
                return (
                  <tr key={r.id} data-testid={`registro-${r.id}`}>
                    <td className="px-3 py-2.5"><p className="font-medium text-slate-800">{r.empleado}</p><p className="text-xs text-slate-400">{r.identificacion} · {r.numero_contrato}</p></td>
                    <td className="px-3 py-2.5"><p className="text-slate-700">{r.nombre}</p><p className="font-mono text-[10px] text-slate-400">ID {r.id}</p></td>
                    <td className="px-3 py-2.5 text-slate-600">v{r.version || 1}</td>
                    <td className="px-3 py-2.5 text-slate-600">{ESTADO[r.estado] || r.estado}</td>
                    <td className="px-3 py-2.5 font-mono text-[10px] text-slate-500" title={r.hash_vigente}>{r.hash_vigente ? `${r.hash_vigente.slice(0, 16)}…` : "—"}</td>
                    <td className="px-3 py-2.5">{ig ? <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ig[1]}`} title={r.integridad.detalle}>{r.integridad.estado !== "verificado" && <AlertTriangle className="h-3 w-3" />}{ig[0]}</span> : <span className="text-xs text-slate-400">Sin verificar</span>}</td>
                    <td className="px-3 py-2.5"><button onClick={() => setView(r)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-vino-700 hover:bg-vino-50" data-testid={`registro-ver-${r.id}`}><Eye className="h-3.5 w-3.5" /> Ver</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {view && <DocViewer url={`/contrataciones/${view.contratacion_id}/documentos/${view.id}/preview`} titulo={view.nombre} subtitulo={`${view.empleado} · versión ${view.version || 1}`} nombreArchivo={`${view.codigo || "documento"}-v${view.version || 1}.pdf`} onClose={() => setView(null)} />}
    </div>
  );
}
